import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { C, F, G, SH } from '../styles/tokens';

const SERVICES = [
  {
    title: 'New GST Registration',
    desc: 'Apply for a new GSTIN online. Mandatory for businesses with turnover above ₹20 lakh (₹10 lakh for special category states) and all e-commerce sellers regardless of turnover.',
  },
  {
    title: 'Monthly GST Returns (GSTR-1 & GSTR-3B)',
    desc: 'Timely filing of GSTR-1 (outward supply details) and GSTR-3B (summary return with tax payment) every month to avoid late fees and interest penalties.',
  },
  {
    title: 'Quarterly Returns (QRMP Scheme)',
    desc: 'Businesses with turnover up to ₹5 crore can opt for quarterly GSTR-1 and GSTR-3B filing under the QRMP scheme with monthly tax payments.',
  },
  {
    title: 'Input Tax Credit (ITC) Reconciliation',
    desc: 'Match your purchase invoices with GSTR-2B to maximise your ITC claims and reduce your GST liability legally.',
  },
  {
    title: 'GST Annual Return (GSTR-9)',
    desc: 'File your GST annual return consolidating all monthly/quarterly returns. Mandatory for businesses with turnover above ₹2 crore.',
  },
  {
    title: 'Multi-state GST Registration',
    desc: 'If you supply goods or services across multiple states, you need a separate GSTIN for each state. We handle all registrations efficiently.',
  },
];

const STEPS = [
  { step: '01', title: 'Eligibility Check', desc: 'We assess your business turnover, type, and state to confirm GST registration requirement and applicable threshold.' },
  { step: '02', title: 'Document Preparation', desc: 'PAN, Aadhaar, business address proof, bank statement, and incorporation documents collected and verified.' },
  { step: '03', title: 'GST Portal Application', desc: 'We file Form GST REG-01 on the GST portal with accurate details to avoid rejection.' },
  { step: '04', title: 'ARN Generation', desc: 'Application Reference Number (ARN) is generated within 15 minutes of submission. We share it with you immediately.' },
  { step: '05', title: 'Verification & Approval', desc: 'GST officer reviews the application. We respond to any queries (Form GST REG-03) on your behalf.' },
  { step: '06', title: 'GSTIN Allotment', desc: 'Your 15-digit GSTIN is allotted and the GST Registration Certificate (Form GST REG-06) is issued within 3–7 working days.' },
];

const FAQS = [
  {
    q: 'Who needs GST registration in India?',
    a: 'Any business with aggregate annual turnover exceeding ₹20 lakh (₹10 lakh for special category states like Uttarakhand, J&K, etc.) must register for GST. E-commerce operators, inter-state suppliers, reverse charge mechanism recipients, and non-resident taxable persons must also register regardless of turnover.',
  },
  {
    q: 'How long does GST registration take?',
    a: 'If all documents are in order, GST registration is typically granted within 3–7 working days. The ARN is generated immediately after submission. If the officer raises a query (GSTR REG-03), it must be responded to within 7 working days.',
  },
  {
    q: 'What is the penalty for not registering under GST?',
    a: 'Businesses that fail to obtain mandatory GST registration are liable to a penalty of 10% of the tax due (minimum ₹10,000) or 100% of the tax due in cases of deliberate tax evasion.',
  },
  {
    q: 'What documents are required for GST registration?',
    a: 'PAN card of the applicant, Aadhaar card, proof of business registration (incorporation certificate / partnership deed), address proof of business (electricity bill, rent agreement), bank account statement or cancelled cheque, and passport-size photograph of the proprietor / directors.',
  },
  {
    q: 'Can I register for GST voluntarily even if my turnover is below the threshold?',
    a: 'Yes. Voluntary GST registration is permitted even if your turnover is below the threshold. This is beneficial if you want to claim input tax credit on purchases, supply to GST-registered businesses, or operate on e-commerce platforms.',
  },
  {
    q: 'What is the difference between GSTR-1 and GSTR-3B?',
    a: 'GSTR-1 is a statement of outward supplies (sales invoices) filed monthly or quarterly. GSTR-3B is a monthly self-declared summary return where you pay the GST liability after offsetting input tax credit. Both must be filed to maintain GST compliance.',
  },
];

const GSTRegistrationPage = () => (
  <>
    <Helmet>
      <title>GST Registration Online India — Get GSTIN in 3 Days | Pracco</title>
      <meta name="description" content="Apply for GST registration online with Pracco & get your GSTIN in 3–7 days. New GST registration, GSTR-1, GSTR-3B, ITC reconciliation & annual return. Free consultation — call +91-9311817126." />
      <meta name="keywords" content="GST registration online India, apply for GSTIN, new GST registration, GST filing services, GSTR-1 filing, GSTR-3B filing, GST return filing, input tax credit, GST annual return, GST compliance India, GST consultant India" />
      <link rel="canonical" href="https://pracco.in/gst-registration" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pracco.in/" },
          { "@type": "ListItem", "position": 2, "name": "GST Registration", "item": "https://pracco.in/gst-registration" },
        ],
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Apply for GST Registration in India",
        "description": "Step-by-step process to get GST registration and obtain GSTIN in India.",
        "totalTime": "P7D",
        "step": STEPS.map((s) => ({
          "@type": "HowToStep",
          "position": parseInt(s.step),
          "name": s.title,
          "text": s.desc,
        })),
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      })}</script>
    </Helmet>

    {/* ── Mini Header ── */}
    <Box component="header" sx={{ px: { xs: 2, md: 6 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, background: C.white }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
        <Box component="img" src={logo} alt="Pracco" sx={{ height: 36 }} />
      </Link>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{
          background: C.whatsapp,
          color: C.white,
          fontFamily: F.body,
          fontWeight: 600,
          fontSize: 13,
          px: 2.5,
          py: 1,
          borderRadius: 2,
          textTransform: 'none',
          '&:hover': { background: C.whatsappDk },
        }}
      >
        Free Consultation
      </Button>
    </Box>

    {/* ── Hero ── */}
    <Box
      component="section"
      sx={{
        background: 'linear-gradient(160deg, #f0fdf4 0%, #F8FAFC 55%, #ecfdf5 100%)',
        px: { xs: 2.5, md: 8 },
        py: { xs: 6, md: 9 },
        textAlign: 'center',
      }}
    >
      <Typography
        component="h1"
        sx={{ fontFamily: F.heading, fontSize: { xs: 28, md: 44 }, fontWeight: 800, color: C.text, lineHeight: 1.15, mb: 2 }}
      >
        GST Registration Online in India
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: { xs: 15, md: 18 }, color: C.textMid, maxWidth: 680, mx: 'auto', lineHeight: 1.7, mb: 4 }}>
        Get your GSTIN in 3–7 working days. New GST registration, monthly return filing, and end-to-end
        compliance — handled by our GST experts. Free consultation.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          component="a"
          href="https://wa.me/9311817126"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<WhatsAppIcon />}
          sx={{
            background: '#10B981',
            color: C.white,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 15,
            px: 4,
            py: 1.5,
            borderRadius: 2.5,
            textTransform: 'none',
            boxShadow: SH.btnGreen,
            '&:hover': { background: '#059669', boxShadow: SH.btnGreenHover },
          }}
        >
          Apply for GST Registration
        </Button>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          sx={{
            fontFamily: F.body,
            fontWeight: 600,
            fontSize: 14,
            px: 3,
            py: 1.5,
            borderRadius: 2.5,
            textTransform: 'none',
            borderColor: '#10B981',
            color: '#10B981',
            '&:hover': { background: '#f0fdf4' },
          }}
        >
          View All Services
        </Button>
      </Box>
    </Box>

    {/* ── Service Types ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        Our GST Services
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        From new GST registration to annual filing — complete GST compliance under one roof.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1100, mx: 'auto' }}>
        {SERVICES.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.title}>
            <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 3, p: 3, height: '100%', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: SH.cardHover } }}>
              <CheckCircleOutlineIcon sx={{ color: '#10B981', mb: 1 }} />
              <Typography sx={{ fontFamily: F.heading, fontSize: 16, fontWeight: 700, color: C.text, mb: 1 }}>{s.title}</Typography>
              <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── Process Steps ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.bg }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        GST Registration Process
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        Our streamlined process gets your GSTIN approved quickly and correctly.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
        {STEPS.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.step}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Typography sx={{ fontFamily: F.heading, fontSize: 34, fontWeight: 800, color: C.border, lineHeight: 1, minWidth: 44 }}>{s.step}</Typography>
              <Box>
                <Typography sx={{ fontFamily: F.heading, fontSize: 15, fontWeight: 700, color: C.text, mb: 0.5 }}>{s.title}</Typography>
                <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── FAQ ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white, maxWidth: 860, mx: 'auto' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 4, textAlign: 'center' }}>
        Frequently Asked Questions — GST Registration
      </Typography>
      {FAQS.map((faq) => (
        <Accordion key={faq.q} disableGutters elevation={0} sx={{ border: `1px solid ${C.border}`, mb: 1.5, borderRadius: '10px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#10B981' }} />}>
            <Typography sx={{ fontFamily: F.body, fontWeight: 600, fontSize: 15, color: C.text }}>{faq.q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.8 }}>{faq.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>

    {/* ── CTA ── */}
    <Box component="section" sx={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)', px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 7 }, textAlign: 'center' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.white, mb: 1.5 }}>
        Apply for GST Registration Today
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 480, mx: 'auto' }}>
        Get your GSTIN in 3–7 days. Our GST experts handle everything — from application to approval.
      </Typography>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{
          background: C.whatsapp,
          color: C.white,
          fontFamily: F.body,
          fontWeight: 700,
          fontSize: 16,
          px: 5,
          py: 1.75,
          borderRadius: 2.5,
          textTransform: 'none',
          boxShadow: SH.btnGreen,
          '&:hover': { background: C.whatsappDk, boxShadow: SH.btnGreenHover },
        }}
      >
        WhatsApp Us Now
      </Button>
    </Box>

    {/* ── Mini Footer ── */}
    <Box component="footer" sx={{ px: { xs: 2.5, md: 8 }, py: 3, background: C.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      <Typography sx={{ fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
        © {new Date().getFullYear()} Pracco Corporate Services Pvt. Ltd. — info@pracco.in · +91-9311817126
      </Typography>
      <Link to="/" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
        ← Back to Home
      </Link>
    </Box>
  </>
);

export default GSTRegistrationPage;
